import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = ({ cart, updateCart, user }) => {
  const navigate = useNavigate();
  const [isGuest, setIsGuest] = useState(!user);
  const [formData, setFormData] = useState({
    company_name: user?.company_name || '',
    company_registration: user?.company_registration || '',
    contact_person: user?.contact_person || '',
    contact_phone: user?.contact_phone || '',
    email: user?.email || '',
  });
  const [processing, setProcessing] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setProcessing(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        total: total
      };

      if (user) {
        orderData.user_id = user.id;
      } else {
        orderData.guest_email = formData.email;
        orderData.guest_name = formData.contact_person;
        orderData.guest_company = formData.company_name;
        orderData.guest_phone = formData.contact_phone;
      }

      const response = await axios.post(`${API}/orders`, orderData);
      
      toast.success('Order placed successfully!');
      updateCart([]);
      
      if (user) {
        navigate('/account');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to process order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="pt-24 pb-16 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl font-black mb-6">YOUR CART IS EMPTY</h1>
          <Button onClick={() => navigate('/shop')} className="bg-[#FF4500] hover:bg-[#FF4500]/90">
            Shop Now
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-5xl mx-auto"
      >
        <h1 className="text-5xl font-black mb-12" data-testid="checkout-title">CHECKOUT</h1>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Checkout Form */}
          <div>
            <h2 className="text-2xl font-bold mb-6">Customer Information</h2>
            
            {!user && (
              <div className="mb-6 p-4 border border-white/10 bg-white/5">
                <p className="text-sm text-white/70 mb-4">
                  You are checking out as a guest. Your details will not be saved.
                </p>
                <Button 
                  onClick={() => navigate('/auth')} 
                  data-testid="register-login-button"
                  variant="outline" 
                  className="border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500]/10"
                >
                  Register or Login to Save Purchase History
                </Button>
              </div>
            )}

            <form onSubmit={handleCheckout} className="space-y-6">
              <div>
                <Label htmlFor="company_name" className="text-white mb-2 block">Company Name</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  type="text"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  data-testid="checkout-company-name-input"
                  disabled={!!user}
                  className="bg-transparent border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="company_registration" className="text-white mb-2 block">Company Registration Number</Label>
                <Input
                  id="company_registration"
                  name="company_registration"
                  type="text"
                  value={formData.company_registration}
                  onChange={handleInputChange}
                  required
                  data-testid="checkout-company-reg-input"
                  disabled={!!user}
                  className="bg-transparent border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="contact_person" className="text-white mb-2 block">Contact Person</Label>
                <Input
                  id="contact_person"
                  name="contact_person"
                  type="text"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  required
                  data-testid="checkout-contact-person-input"
                  disabled={!!user}
                  className="bg-transparent border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="contact_phone" className="text-white mb-2 block">Contact Phone Number</Label>
                <Input
                  id="contact_phone"
                  name="contact_phone"
                  type="tel"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  required
                  data-testid="checkout-phone-input"
                  disabled={!!user}
                  className="bg-transparent border-white/20 text-white"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-white mb-2 block">Contact Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  data-testid="checkout-email-input"
                  disabled={!!user}
                  className="bg-transparent border-white/20 text-white"
                />
              </div>

              <div className="pt-6">
                <h3 className="text-xl font-bold mb-4">Payment Information</h3>
                <div className="p-6 border-2 border-[#D4AF37]/50 bg-[#D4AF37]/10 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] text-xl">ℹ️</span>
                    </div>
                    <div>
                      <p className="text-[#D4AF37] font-bold mb-2">Payment Gateway Coming Soon</p>
                      <p className="text-white/80 text-sm mb-2">
                        Our online payment system is not yet live. Once your order is placed, you will receive banking details on your invoice or quote.
                      </p>
                      <p className="text-white/60 text-xs">
                        For urgent enquiries, please contact us directly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={processing}
                data-testid="place-order-button"
                className="w-full bg-[#FF4500] hover:bg-[#FF4500]/90 text-white uppercase tracking-widest font-bold px-8 py-6 text-lg neon-glow-hover"
              >
                {processing ? 'Processing...' : 'Place Order'}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
            <div className="border border-white/10 p-6 space-y-4">
              {cart.map((item, index) => (
                <div key={item.id} data-testid={`summary-item-${index}`} className="flex justify-between pb-4 border-b border-white/10 last:border-0">
                  <div>
                    <p className="font-bold" data-testid={`summary-item-name-${index}`}>{item.name}</p>
                    <p className="text-sm text-white/50">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-bold text-[#FF4500]" data-testid={`summary-item-total-${index}`}>R{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
              
              <div className="pt-4 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">Total</span>
                  <span className="text-2xl font-black text-[#FF4500]" data-testid="checkout-total">R{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Checkout;